import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import { getProfile } from "@/lib/profile-api";
import {
  getPerStateLicensing,
  type Citation,
  type LocalizedString,
  type PerStateLicensing,
} from "@/data/per-state-licensing";

/**
 * ApplyPage — the per-state apply-for-licence + appointment-workflow surface.
 *
 * URL shape: `/apply/:stateCode` (lower- or upper-case 2-letter code).
 * If `:stateCode` is omitted (`/apply`), we fall back to the learner's
 * `current_focus_state` from their profile.
 *
 * This page covers BOTH paired tickets (ROADS-426 + ROADS-435) on a
 * single surface so the learner doesn't bounce across two screens for
 * what is, in practice, the same booking-day workflow:
 *
 *   1. Apply: where, what to bring, fees, foreign-license reciprocity,
 *      what to expect on test day.
 *   2. Appointment: book here, lead time, walk-in conditions, portal
 *      languages, best-time signal.
 *
 * Status-agnostic (ROADS-436): every document description reads as a
 * category, not as advice for a specific applicant population. The
 * underlying data module enforces this with a contract test.
 *
 * Phase-0 scope: CA + TX + NY + FL + NJ + IL + AZ + WA + MA + GA.
 * Per-state shape proven end-to-end across all 10 launch states.
 * CA + TX + NY + FL + NJ + IL + AZ + WA + MA + GA is the
 * 10-launch-state monetization set.
 */
export default function ApplyPage() {
  const { t, i18n } = useTranslation();
  const params = useParams<{ stateCode?: string }>();
  const [resolvedCode, setResolvedCode] = useState<string | null>(
    params.stateCode ? params.stateCode.toUpperCase() : null,
  );
  const [profileLoading, setProfileLoading] = useState<boolean>(!params.stateCode);

  // If the route has no :stateCode, derive it from the user's profile.
  useEffect(() => {
    if (params.stateCode) {
      setResolvedCode(params.stateCode.toUpperCase());
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        setResolvedCode(profile?.current_focus_state ?? null);
      } catch {
        if (!cancelled) setResolvedCode(null);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params.stateCode]);

  const content = useMemo<PerStateLicensing | undefined>(
    () => (resolvedCode ? getPerStateLicensing(resolvedCode) : undefined),
    [resolvedCode],
  );

  const lang = pickLang(i18n.language);

  return (
    <LearnerPortalShell pageTitle={t("apply.pageTitle")}>
      <div className="bg-slate-50">
        {profileLoading ? (
          <div
            className="mx-auto max-w-4xl px-4 py-12 text-neutral-600 sm:px-8"
            role="status"
          >
            {t("apply.loading")}
          </div>
        ) : !content ? (
          <NotCovered code={resolvedCode} />
        ) : (
          <ApplyContentView content={content} lang={lang} />
        )}
      </div>
    </LearnerPortalShell>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

type Lang = "en" | "es";

function pickLang(raw: string | undefined): Lang {
  const primary = (raw ?? "en").split("-")[0].toLowerCase();
  return primary === "es" ? "es" : "en";
}

function localized(s: LocalizedString, lang: Lang): string {
  return s[lang] ?? s.en;
}

// ── Subviews ────────────────────────────────────────────────────────────────

function NotCovered({ code }: { code: string | null }) {
  const { t } = useTranslation();
  return (
    <div
      className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-8"
      role="alert"
    >
      <h1 className="font-display text-2xl font-bold text-brand-primary sm:text-3xl">
        {t("apply.notCovered.title")}
      </h1>
      <p className="mt-md text-neutral-700">
        {code
          ? t("apply.notCovered.body", { code })
          : t("apply.notCovered.bodyNoCode")}
      </p>
    </div>
  );
}

function ApplyContentView({
  content,
  lang,
}: {
  content: PerStateLicensing;
  lang: Lang;
}) {
  const { t } = useTranslation();
  const stateName = localized(content.displayName, lang);

  return (
    <article
      className="mx-auto max-w-4xl px-4 py-10 sm:px-8 sm:py-14"
      data-testid={`apply-${content.stateCode.toLowerCase()}`}
    >
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
          {t("apply.eyebrow")}
        </p>
        <h1 className="mt-xs font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("apply.title", { state: stateName })}
        </h1>
        <p className="mt-sm text-neutral-700 sm:text-lg">
          {t("apply.subtitle", { state: stateName })}
        </p>
      </header>

      {/* ── Where to apply ───────────────────────────────────────────── */}
      <section
        aria-labelledby="apply-where-heading"
        className="mt-xl rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="apply-where-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("apply.where.heading")}
        </h2>
        <p className="mt-sm text-neutral-800">
          {t("apply.where.body", { state: stateName })}
        </p>
        <a
          href={content.apply.officeFinderUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-md inline-flex items-center gap-2 rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("apply.where.findOffice")}
          <span aria-hidden="true">→</span>
          <span className="sr-only">{t("apply.opensInNewTab")}</span>
        </a>
        <CitationLink citation={content.apply.officeFinderCitation} />
      </section>

      {/* ── Required documents ──────────────────────────────────────── */}
      <section
        aria-labelledby="apply-docs-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="apply-docs-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("apply.documents.heading")}
        </h2>
        <p className="mt-sm text-neutral-700">
          {t("apply.documents.intro", { state: stateName })}
        </p>
        <ul className="mt-md space-y-md" aria-label={t("apply.documents.listAriaLabel")}>
          {content.apply.requiredDocuments.map((doc) => (
            <li
              key={doc.id}
              className="rounded-md border border-neutral-200 bg-slate-50 p-md"
            >
              <h3 className="font-semibold text-brand-primary">
                {localized(doc.label, lang)}
              </h3>
              <p className="mt-xs text-sm text-neutral-800">
                {localized(doc.detail, lang)}
              </p>
              <CitationLink citation={doc.citation} compact />
            </li>
          ))}
        </ul>
      </section>

      {/* ── Foreign license ─────────────────────────────────────────── */}
      <section
        aria-labelledby="apply-foreign-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="apply-foreign-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("apply.foreign.heading")}
        </h2>
        <p className="mt-sm text-neutral-800">
          {localized(content.apply.foreignLicenseReciprocity.summary, lang)}
        </p>
        <CitationLink
          citation={content.apply.foreignLicenseReciprocity.citation}
        />
      </section>

      {/* ── Fees ────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="apply-fees-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="apply-fees-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("apply.fees.heading")}
        </h2>
        <p className="mt-sm text-2xl font-semibold text-brand-primary">
          {t("apply.fees.amount", { amount: content.apply.fees.applicationUsd })}
        </p>
        <p className="mt-sm text-neutral-800">
          {localized(content.apply.fees.notes, lang)}
        </p>
        <CitationLink citation={content.apply.fees.citation} />
      </section>

      {/* ── Wait times + test day ───────────────────────────────────── */}
      <section
        aria-labelledby="apply-waittime-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="apply-waittime-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("apply.waitTimes.heading")}
        </h2>
        <p className="mt-sm text-neutral-800">
          {localized(content.apply.waitTimes, lang)}
        </p>
        <h3 className="mt-md font-semibold text-brand-primary">
          {t("apply.testDay.heading")}
        </h3>
        <p className="mt-sm text-neutral-800">
          {localized(content.apply.testDayExpectations, lang)}
        </p>
      </section>

      {/* ── Appointment workflow ─────────────────────────────────────── */}
      <section
        aria-labelledby="apply-appointment-heading"
        className="mt-2xl rounded-lg border-2 border-brand-accent bg-amber-50 p-md sm:p-lg"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">
          {t("apply.appointment.eyebrow")}
        </p>
        <h2
          id="apply-appointment-heading"
          className="mt-xs font-display text-2xl font-bold text-brand-primary sm:text-3xl"
        >
          {t("apply.appointment.heading")}
        </h2>

        {/* Booking CTA */}
        <p className="mt-md text-neutral-800">
          {t(`apply.appointment.policy.${content.appointment.policy}`)}
        </p>
        <a
          href={content.appointment.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-md inline-flex items-center gap-2 rounded-md bg-brand-accent px-md py-sm text-sm font-semibold text-brand-primary hover:bg-amber-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
        >
          {t("apply.appointment.bookCta", { state: stateName })}
          <span aria-hidden="true">→</span>
          <span className="sr-only">{t("apply.opensInNewTab")}</span>
        </a>
        <CitationLink citation={content.appointment.policyCitation} />

        {/* Lead time */}
        <h3 className="mt-lg font-semibold text-brand-primary">
          {t("apply.appointment.leadTime.heading")}
        </h3>
        <p className="mt-sm text-neutral-800">
          {localized(content.appointment.bookingLeadTime, lang)}
        </p>

        {/* Walk-ins */}
        <h3 className="mt-md font-semibold text-brand-primary">
          {t("apply.appointment.walkIn.heading")}
        </h3>
        <p className="mt-sm text-neutral-800">
          {localized(content.appointment.walkInConditions, lang)}
        </p>

        {/* Languages */}
        <h3 className="mt-md font-semibold text-brand-primary">
          {t("apply.appointment.languages.heading")}
        </h3>
        <p className="mt-sm text-neutral-800">
          {t("apply.appointment.languages.body", {
            count: content.appointment.portalLanguages.length,
          })}
        </p>
        <ul
          className="mt-sm flex flex-wrap gap-2"
          aria-label={t("apply.appointment.languages.listAriaLabel")}
        >
          {content.appointment.portalLanguages.map((code) => (
            <li
              key={code}
              className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-brand-primary border border-neutral-300"
            >
              {code.toUpperCase()}
            </li>
          ))}
        </ul>
        <CitationLink citation={content.appointment.languagesCitation} />

        {/* Best-time signal (editorial) */}
        <h3 className="mt-md font-semibold text-brand-primary">
          {t("apply.appointment.bestTime.heading")}
        </h3>
        <p className="mt-sm text-neutral-800">
          {localized(content.appointment.bestTimeSignal, lang)}
        </p>
        <p className="mt-xs text-xs italic text-neutral-600">
          {t("apply.appointment.bestTime.editorialDisclaimer")}
        </p>

        {/* Documents cross-ref */}
        {content.appointment.documentsCrossReference && (
          <p className="mt-md rounded-md border border-amber-300 bg-white p-sm text-sm text-neutral-800">
            {t("apply.appointment.documentsCrossRef")}
          </p>
        )}
      </section>
    </article>
  );
}

function CitationLink({
  citation,
  compact,
}: {
  citation: Citation;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <p
      className={`${compact ? "mt-sm" : "mt-md"} text-xs text-neutral-600`}
    >
      <a
        href={citation.url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("apply.citation.sourceLabel")}
      </a>
      <span className="mx-1" aria-hidden="true">·</span>
      <span>
        {t("apply.citation.retrievedOn", { date: citation.retrievedOn })}
      </span>
      <span className="sr-only">{t("apply.opensInNewTab")}</span>
    </p>
  );
}
